import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Gift, Megaphone, Plus, ShieldCheck, Users } from 'lucide-react';
import { PageHeader } from '../components/Layout';
import { Button, ErrorNote, Skeleton, StatTile, Tabs } from '../components/ui';
import { api } from '../lib/api';
import { formatCredits, formatNumber } from '../lib/format';
import { useT } from '../lib/i18n';
import type { ReferralCampaign, ReferralOverview } from '../lib/types';
import { ActivityTab } from './referrals/Activity';
import { CampaignDrawer } from './referrals/CampaignDrawer';
import { CampaignEditor } from './referrals/CampaignEditor';
import { CampaignsTab } from './referrals/Campaigns';
import { RedemptionsTab } from './referrals/Redemptions';
import { RewardsTab } from './referrals/Rewards';

type ReferralTab = 'campaigns' | 'redemptions' | 'rewards' | 'activity';

const TABS: ReferralTab[] = ['campaigns', 'redemptions', 'rewards', 'activity'];

function isTab(value: string | null): value is ReferralTab {
  return TABS.includes(value as ReferralTab);
}

/**
 * The referral program.
 *
 * Four views of one thing: the **offers** (campaigns), the **friends** who
 * joined through them, the **rewards** they paid, and the **log** behind all
 * three. The tab and an open campaign live in the url, so a link to "the
 * friends who joined the spring campaign" is a link, and the way back from
 * an account is the browser's back button.
 */
export function ReferralsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<ReferralCampaign | 'new' | null>(null);

  const tab: ReferralTab = isTab(params.get('tab')) ? (params.get('tab') as ReferralTab) : 'campaigns';
  const openCampaign = params.get('campaign');

  const overview = useQuery({
    queryKey: ['referrals', 'overview'],
    queryFn: () => api.get<ReferralOverview>('/api/referrals/overview'),
  });

  /** Switching tab drops the other tab's filters — they mean nothing here. */
  const switchTab = (next: ReferralTab) => setParams({ tab: next });

  const open = (campaignId: string) => {
    const next = new URLSearchParams(params);
    next.set('campaign', campaignId);
    setParams(next);
  };

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('campaign');
    setParams(next);
  };

  const data = overview.data;

  return (
    <>
      <PageHeader
        title={t('referrals.title')}
        description={t('referrals.description')}
        action={
          <Button variant="primary" icon={<Plus size={15} />} onClick={() => setEditing('new')}>
            {t('referrals.newCampaign')}
          </Button>
        }
      />

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {overview.isPending || !data ? (
            Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-24" />)
          ) : (
            <>
              <StatTile
                tone="brand"
                icon={<Megaphone size={14} />}
                label={t('referrals.tile.live')}
                value={formatNumber(data.campaigns.live)}
                caption={t('referrals.tile.liveCaption', {
                  scheduled: formatNumber(data.campaigns.scheduled),
                  ended: formatNumber(data.campaigns.ended),
                })}
                onClick={() => switchTab('campaigns')}
              />
              <StatTile
                icon={<Users size={14} />}
                label={t('referrals.tile.joined')}
                value={formatNumber(data.referrals.qualified + data.referrals.pending)}
                caption={t('referrals.tile.joinedCaption', {
                  pending: formatNumber(data.referrals.pending),
                })}
                onClick={() => switchTab('redemptions')}
              />
              <StatTile
                tone="good"
                icon={<Gift size={14} />}
                label={t('referrals.tile.paid')}
                value={formatNumber(data.rewards.granted)}
                caption={t('referrals.tile.paidCaption', {
                  daylights: formatCredits(data.rewards.daylights),
                  days: formatNumber(data.rewards.trialDays),
                })}
                onClick={() => switchTab('rewards')}
              />
              <StatTile
                tone={data.rewards.failed > 0 ? 'danger' : 'neutral'}
                icon={<AlertTriangle size={14} />}
                label={t('referrals.tile.failed')}
                value={formatNumber(data.rewards.failed)}
                caption={t('referrals.tile.failedCaption')}
                onClick={() => setParams({ tab: 'rewards', status: 'failed' })}
              />
            </>
          )}
        </div>
        {overview.isError && <ErrorNote error={overview.error} />}

        {data && (
          <div className="flex items-start gap-2 rounded-xl border border-line bg-surface px-3.5 py-3 text-xs text-ink-2 sm:px-4">
            <ShieldCheck size={15} className="mt-0.5 shrink-0 text-good" />
            <p className="min-w-0">
              {data.policy.redeemWindowHours > 0
                ? t('referrals.policy.window', { hours: data.policy.redeemWindowHours })
                : t('referrals.policy.closed')}
              {' · '}
              {t(data.policy.requireSignIn ? 'referrals.policy.signIn' : 'referrals.policy.noSignIn')}
              {' · '}
              {data.policy.maxRedeemsPerIp > 0
                ? t('referrals.policy.ip', {
                    count: data.policy.maxRedeemsPerIp,
                    hours: data.policy.ipWindowHours,
                  })
                : t('referrals.policy.noIp')}
            </p>
          </div>
        )}

        <Tabs
          label={t('referrals.tabsLabel')}
          value={tab}
          onChange={switchTab}
          tabs={[
            { id: 'campaigns', label: t('referrals.tab.campaigns') },
            { id: 'redemptions', label: t('referrals.tab.redemptions') },
            {
              id: 'rewards',
              label: t('referrals.tab.rewards'),
              badge: data?.rewards.failed,
            },
            { id: 'activity', label: t('referrals.tab.activity') },
          ]}
        />

        {tab === 'campaigns' && (
          <CampaignsTab onOpen={open} onCreate={() => setEditing('new')} />
        )}
        {tab === 'redemptions' && <RedemptionsTab />}
        {tab === 'rewards' && <RewardsTab />}
        {tab === 'activity' && <ActivityTab />}
      </div>

      {openCampaign && !editing && (
        <CampaignDrawer
          campaignId={openCampaign}
          onClose={close}
          onEdit={(campaign) => setEditing(campaign)}
          onShow={(next, campaignId) => setParams({ tab: next, campaignId })}
        />
      )}

      {editing && (
        <CampaignEditor
          campaign={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            // A new campaign opens straight away, so the operator sees what
            // was created; an edit goes back to the campaign it came from.
            const next = new URLSearchParams(params);
            next.set('campaign', saved.id);
            setParams(next);
          }}
        />
      )}
    </>
  );
}
